import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service — ForexMind AI" },
      { name: "description", content: "Terms governing your use of the ForexMind AI platform." },
      { name: "robots", content: "index,follow" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "ForexMind AI" },
      { property: "og:title", content: "Terms of Service — ForexMind AI" },
      { property: "og:description", content: "Terms governing your use of the ForexMind AI platform." },
      { property: "og:url", content: "https://forexmind.ai/terms" },
      { property: "og:image", content: "https://forexmind.ai/og-image.png" },
      { property: "og:image:secure_url", content: "https://forexmind.ai/og-image.png" },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Terms of Service — ForexMind AI" },
      { name: "twitter:description", content: "Terms governing your use of the ForexMind AI platform." },
      { name: "twitter:image", content: "https://forexmind.ai/og-image.png" },
    ],
    links: [{ rel: "canonical", href: "https://forexmind.ai/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <main className="mx-auto max-w-3xl px-6 py-16 text-foreground">
      <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">← Back to home</Link>
      <h1 className="mt-6 text-3xl font-bold">Terms of Service</h1>
      <p className="mt-2 text-xs text-muted-foreground">Last updated: June 2026</p>
      <div className="mt-8 space-y-6 text-sm leading-relaxed text-muted-foreground">
        <p className="rounded-lg border border-border bg-card p-4">
          These Terms govern your use of ForexMind AI. By creating an account, accessing
          the service, or receiving signals via Telegram, you agree to be bound by these
          Terms. If you do not agree, do not use the service.
        </p>
        <h2 className="text-lg font-semibold text-foreground">1. Acceptance</h2>
        <p>By creating an account you agree to these terms and our <Link to="/privacy" className="underline">Privacy Policy</Link> and <Link to="/disclaimer" className="underline">Risk Disclaimer</Link>.</p>
        <h2 className="text-lg font-semibold text-foreground">2. Service</h2>
        <p>ForexMind AI provides AI-generated forex market analysis and trading signals. Signals are educational only — see the Risk Disclaimer.</p>
        <h2 className="text-lg font-semibold text-foreground">3. Subscriptions</h2>
        <p>Paid plans renew automatically until cancelled. You can manage or cancel your subscription at any time from your account.</p>
        <h2 className="text-lg font-semibold text-foreground">3a. Refund Policy</h2>
        <div className="rounded-lg border border-border bg-card p-4">
          <p className="font-semibold text-foreground">7-day money-back guarantee.</p>
          <p className="mt-1">
            If you are unsatisfied within 7 days of your first payment, contact{" "}
            <a className="underline" href="mailto:billing@forexmind.ai">billing@forexmind.ai</a>{" "}
            for a full refund. No questions asked.
          </p>
        </div>
        <h2 className="text-lg font-semibold text-foreground">4. Acceptable use</h2>
        <p>You agree not to abuse the service, attempt to bypass security controls, scrape data at scale, or share your account credentials. We may suspend accounts that violate these terms.</p>
        <h2 className="text-lg font-semibold text-foreground">5. No warranty</h2>
        <p>The service is provided "as is" without warranties of any kind. To the maximum extent permitted by law, ForexMind AI is not liable for any trading losses, indirect, consequential, or incidental damages arising from use of the platform.</p>
        <h2 className="text-lg font-semibold text-foreground">6. Termination</h2>
        <p>You can delete your account at any time from Settings. We may terminate accounts for breach of these terms with reasonable notice.</p>
        <h2 className="text-lg font-semibold text-foreground">7. Contact</h2>
        <p>
          Billing, subscriptions, and refunds:{" "}
          <a className="underline" href="mailto:billing@forexmind.ai">billing@forexmind.ai</a>.
          Legal notices, compliance, privacy, and terms:{" "}
          <a className="underline" href="mailto:legal@forexmind.ai">legal@forexmind.ai</a>.
          General questions:{" "}
          <a className="underline" href="mailto:support@forexmind.ai">support@forexmind.ai</a>.
        </p>
      </div>
    </main>
  );
}
